import type {
  AddressElementComponent,
  PaymentElementComponent,
} from '@stripe/react-stripe-js';
import type {
  Stripe,
  StripeElements,
  StripeElementsOptions,
} from '@stripe/stripe-js';
import {
  ComponentType,
  createContext,
  FunctionComponent,
  PropsWithChildren,
  ReactElement,
  useContext,
} from 'react';
import { useStripeKey } from './stripe-key-context';
import { StripeModuleProductionProvider } from './StripeProductionProvider';

export type PartialStripe = Pick<Stripe, 'confirmSetup'>;

export type StripeModuleProviderProps = PropsWithChildren<{
  stripeKey: string;
}>;

export interface StripeModuleInjection {
  stripe: Stripe | null;
  Elements: ComponentType<
    PropsWithChildren<{ stripe: Stripe; options?: StripeElementsOptions }>
  >;
  PaymentElement: PaymentElementComponent;
  AddressElement: AddressElementComponent;
  useElements: () => StripeElements | null;
  useStripe: () => PartialStripe | null;
}

export const StripeModuleProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const stripeKey = useStripeKey();
  if (!stripeKey) {
    return children as ReactElement;
  }

  return (
    <StripeModuleProductionProvider stripeKey={stripeKey}>
      {children}
    </StripeModuleProductionProvider>
  );
};

export const StripeModuleContext = createContext<StripeModuleInjection | null>(
  null,
);

export const useStripeModule = () => {
  const stripeModule = useContext(StripeModuleContext);

  if (!stripeModule) {
    throw new Error('stripeModule not loaded');
  }

  return stripeModule;
};
